import ContactNavigation from 'components/ContactNavigation';
import SiteThemeProvider from 'components/SiteTheme';
import TmsConnectionBoundary from "components/TmsConnectionBoundary";
import "App.css";
import "components/SiteContentLayout.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import Navigation from "components/Navigation";
import Footer from "components/Footer";
import CartProvider from "components/commerce/CartProvider";
import CartPage from "pages/CartPage";
import StoreCheckoutPage from "pages/StoreCheckoutPage";
import BalanceCheckoutPage from "pages/BalanceCheckoutPage";
import PartnerPage from "pages/PartnerPage";
import StoreOrderPage, { StoreOrdersPage } from "pages/StoreOrderPage";
import ScrollToTop from "components/ScrollToTop";
import ScrollProgress from "components/ScrollProgress";
import HeroParallaxController from "components/HeroParallaxController";
import CustomCursor from "components/CustomCursor";
import SeoRouteMetadata from "components/SeoRouteMetadata";
import ErrorBoundary from "components/ErrorBoundary";
import { Toaster } from "sonner";
import HomePage from "pages/HomePage";
import TMSPage from "pages/TMSPage";
import PaperworkPage, { PaperworkLegacyRedirect } from "pages/PaperworkPage";
import PaperworkCheckoutPage from "pages/PaperworkCheckoutPage";
import CareersPage from "pages/CareersPage";
import CareersAssessmentPage from "pages/CareersAssessmentPage";
import CompaniesPage from "pages/CompaniesPage";
import AboutPage from "pages/AboutPage";
import ContactPage from "pages/ContactPage";
import NotFoundPage from "pages/NotFoundPage";
import WebinarsPage from "pages/WebinarsPage";
import WebinarCourseDetailPage from "pages/WebinarCourseDetailPage";
import WelcomePage from "pages/WelcomePage";
import SpeakerProfilePage from "pages/SpeakerProfilePage";
import AdminLayoutBoundary from "components/admin/AdminLayoutBoundary";
import BlogPage from "pages/BlogPage";
import BlogPostPage from "pages/BlogPostPage";
import AgreementSignPage from "pages/AgreementSignPage";
import CustomerAccessPage from "pages/CustomerAccessPage";
import CustomerIdentityPreviewPage from "pages/CustomerIdentityPreviewPage";
import CustomerPortalPreviewPage from "pages/CustomerPortalPreviewPage";
import CommerceW3PreviewPage from "pages/CommerceW3PreviewPage";
import CustomerDeliveryW4PreviewPage from "pages/CustomerDeliveryW4PreviewPage";
import { DownloadConfirmPage, EmailPreferencesPage } from "pages/DownloadAccessPages";
import { PrivacyPolicyPage, TermsOfUsePage } from "pages/LegalPages";
import {
  CompanyApplicationSubmittedPage,
  DataQsWebinarPage,
  ObsolescencePage,
  ResourceDetailPage,
  ResourcesPage,
  ServiceDetailPage,
  ServicesPage,
} from "pages/SeoRoutePages";

const pageTransition = {
  initial: { opacity: 0, y: 16, filter: 'blur(8px)' },
  animate: {
    opacity: 1, y: 0, filter: 'blur(0px)',
    transition: { duration: 0.55, ease: [0.25, 0.46, 0.45, 0.94] },
  },
  exit: {
    opacity: 0, y: -10, filter: 'blur(4px)',
    transition: { duration: 0.25, ease: [0.25, 0.46, 0.45, 0.94] },
  },
};

// Visitors who ask for less motion get an instant page change: no blur, slide or fade.
// The zero-length animation still completes, so 'magellan:page-ready' keeps firing.
const reducedPageTransition = {
  initial: { opacity: 1 },
  animate: { opacity: 1, transition: { duration: 0 } },
  exit: { opacity: 1, transition: { duration: 0 } },
};

function AnimatedRoutes() {
  const location = useLocation();
  const reduceMotion = useReducedMotion();
  const transition = reduceMotion ? reducedPageTransition : pageTransition;

  return (
    <AnimatePresence mode="wait">
      <motion.div key={location.pathname} {...transition} onAnimationComplete={() => {
        if (location.pathname === '/' && location.hash === '#contact') window.dispatchEvent(new Event('magellan:page-ready'));
      }}>
        <Routes location={location}>
          <Route path="/" element={<HomePage />} />
          <Route path="/tms" element={<TMSPage />} />
          <Route path="/paperwork" element={<PaperworkPage />} />
          <Route path="/cart" element={<CartPage />} />
          <Route path="/cart/orders" element={<StoreOrdersPage />} />
          <Route path="/checkout" element={<StoreCheckoutPage />} />
          <Route path="/checkout/balance/:offerId" element={<BalanceCheckoutPage />} />
          <Route path="/partner-with-us" element={<PartnerPage />} />
          <Route path="/checkout/return" element={<StoreOrderPage />} />
          <Route path="/paperwork/:packageId/checkout" element={<PaperworkCheckoutPage />} />
          <Route path="/paperwork/premium/apply" element={<PaperworkCheckoutPage packageId="premium" />} />
          <Route path="/checkout/success" element={<PaperworkCheckoutPage resultState="success" />} />
          <Route path="/checkout/pending" element={<PaperworkCheckoutPage resultState="pending" />} />
          <Route path="/checkout/failed" element={<PaperworkCheckoutPage resultState="failed" />} />
          <Route path="/paperwork/basic" element={<PaperworkLegacyRedirect packageKey="basic" />} />
          <Route path="/paperwork/standard" element={<PaperworkLegacyRedirect packageKey="standard" />} />
          <Route path="/paperwork/premium" element={<PaperworkLegacyRedirect packageKey="premium" />} />
          <Route path="/paperwork/packages/:packageId" element={<PaperworkLegacyRedirect />} />
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/services/premium-freight-paperwork" element={<ServiceDetailPage serviceKey="premium-freight-paperwork" />} />
          <Route path="/services/tms-operations" element={<ServiceDetailPage serviceKey="tms-operations" />} />
          <Route path="/services/tms-platform" element={<ServiceDetailPage serviceKey="tms-platform" />} />
          <Route path="/services/paperwork-compliance" element={<ServiceDetailPage serviceKey="paperwork-compliance" />} />
          <Route path="/services/driver-training" element={<ServiceDetailPage serviceKey="driver-training" />} />
          <Route path="/services/driver-recruiting" element={<ServiceDetailPage serviceKey="driver-recruiting" />} />
          <Route path="/services/safety-systems" element={<ServiceDetailPage serviceKey="safety-systems" />} />
          <Route path="/services/new-entrant-audit-preparation" element={<ServiceDetailPage serviceKey="new-entrant-audit-preparation" />} />
          <Route path="/services/specialized-freight-readiness" element={<ServiceDetailPage serviceKey="specialized-freight-readiness" />} />
          <Route path="/services/:slug" element={<ServiceDetailPage />} />
          <Route path="/careers" element={<CareersPage />} />
          <Route path="/careers/assessment" element={<CareersAssessmentPage />} />
          <Route path="/work-with-us" element={<CareersPage />} />
          <Route path="/work-with-us/apply" element={<CareersPage />} />
          <Route path="/work-with-us/application-received" element={<CareersPage />} />
          <Route path="/companies" element={<CompaniesPage />} />
          <Route path="/companies/apply" element={<CompaniesPage />} />
          <Route path="/companies/application-submitted" element={<TmsConnectionBoundary><CompanyApplicationSubmittedPage /></TmsConnectionBoundary>} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/terms-of-use" element={<TermsOfUsePage />} />
          <Route path="/customer/access" element={<CustomerAccessPage />} />
          <Route path="/customer/access/*" element={<CustomerAccessPage />} />
          <Route path="/customer/identity-preview" element={<CustomerIdentityPreviewPage />} />
          <Route path="/customer/portal-preview" element={<CustomerPortalPreviewPage />} />
          <Route path="/commerce/preview" element={<CommerceW3PreviewPage />} />
          <Route path="/customer/delivery-preview" element={<CustomerDeliveryW4PreviewPage />} />
          <Route path="/agreement/:token" element={<TmsConnectionBoundary><AgreementSignPage /></TmsConnectionBoundary>} />
          <Route path="/downloads/confirm/:token" element={<Navigate to="/customer/access" replace />} />
          <Route path="/email-preferences" element={<TmsConnectionBoundary><EmailPreferencesPage /></TmsConnectionBoundary>} />
          <Route path="/welcome" element={<WelcomePage />} />
          <Route path="/webinars" element={<WebinarsPage />} />
          <Route path="/webinars/dataqs-ticket-challenge" element={<WebinarCourseDetailPage />} />
          <Route path="/webinars/complete-webinar-series" element={<WebinarCourseDetailPage />} />
          <Route path="/webinars/company-training" element={<WebinarsPage />} />
          <Route path="/webinars/:slug" element={<WebinarCourseDetailPage />} />
          <Route path="/speakers/:slug" element={<SpeakerProfilePage />} />
          <Route path="/obsolescence" element={<ObsolescencePage />} />
          <Route path="/book-a-meeting" element={<Navigate to="/contact" replace />} />
          <Route path="/resources" element={<ResourcesPage />} />
          <Route path="/resources/:slug" element={<ResourceDetailPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/category/:category" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </motion.div>
    </AnimatePresence>
  );
}

function App() {
  return (
    <CartProvider><BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <SiteThemeProvider><ErrorBoundary>
        <AdminLayoutBoundary>
        <Toaster position="top-right" richColors closeButton />
        <CustomCursor />
        <SeoRouteMetadata />
        <ScrollToTop />
        <ContactNavigation />
        <ScrollProgress />
        <HeroParallaxController />
        <Navigation />
        <AnimatedRoutes />
        <Footer />
        </AdminLayoutBoundary>
      </ErrorBoundary></SiteThemeProvider>
    </BrowserRouter></CartProvider>
  );
}

export default App;
